import type { AxiosInstance, AxiosResponse } from 'axios';
import { ZodError, type ZodSchema } from 'zod';

import type {
  Logger,
  SimpleApiValidatorConfig,
  StoredValidationError,
  ValidationError,
  ValidationInterceptor,
} from '../types';
import { extractApiPath, matchPattern } from '../utils/pattern-matcher';

export class SimpleApiValidator {
  private schemas: Record<string, ZodSchema>;
  private enabled: boolean;
  private enableConsoleLogging: boolean;
  private strictMode: boolean;
  private showUI: boolean;
  private logger: Logger;
  private storedErrors: Record<string, StoredValidationError> = {};
  private subscribers: (() => void)[] = [];

  constructor(config: SimpleApiValidatorConfig) {
    this.schemas = config.schemas;
    this.enabled = config.enabled ?? true;
    this.enableConsoleLogging = config.enableConsoleLogging ?? false;
    this.strictMode = config.strictMode ?? false;
    this.showUI = config.showUI ?? true;
    this.logger = config.logger ?? console;
  }

  /**
   * Create the validation interceptor
   */
  private createInterceptor(): ValidationInterceptor {
    return (response: AxiosResponse) => {
      if (!this.enabled) {
        return response;
      }

      try {
        const method = response.config.method?.toUpperCase() || 'GET';
        const url = extractApiPath(response.config.url || '');

        if (this.enableConsoleLogging) {
          this.logger.debug(`🔍 Checking validation for: ${method} ${url}`);
        }

        // Find matching schema
        let matchedSchema: ZodSchema | undefined;
        let matchedPattern: string | undefined;

        for (const [pattern, schema] of Object.entries(this.schemas)) {
          if (matchPattern(pattern, method, url)) {
            matchedSchema = schema;
            matchedPattern = pattern;

            if (this.enableConsoleLogging) {
              this.logger.debug(`✅ Pattern match found: ${pattern}`);
            }
            break;
          }
        }

        if (!matchedSchema) {
          if (this.enableConsoleLogging) {
            this.logger.debug(`❌ No matching schema found for: ${method} ${url}`);
          }
          return response;
        }

        // Validate the response
        try {
          matchedSchema.parse(response.data);

          if (this.enableConsoleLogging) {
            this.logger.debug(`✅ API Validation Passed: ${method} ${url}`);
          }
        } catch (error) {
          if (error instanceof ZodError) {
            this.handleValidationError(error, {
              endpoint: url,
              method,
              pattern: matchedPattern,
              response: response.data,
            });
          }
        }
      } catch (error) {
        this.logger.error('API Validator Error:', error);
      }

      return response;
    };
  }

  /**
   * Handle validation errors
   */
  private handleValidationError(
    error: ZodError,
    context: {
      endpoint: string;
      method: string;
      pattern?: string;
      response: unknown;
    },
  ): void {
    const validationError: ValidationError = {
      endpoint: context.endpoint,
      method: context.method,
      pattern: context.pattern,
      errors: error.issues.map((issue) => ({
        path: issue.path,
        message: issue.message,
      })),
      response: context.response,
      timestamp: new Date(),
    };

    // Store error for UI
    const errorKey = `${context.method} ${context.endpoint}`;
    this.storeError(errorKey, validationError);

    if (this.enableConsoleLogging) {
      this.logger.error(`❌ API Validation Failed: ${context.method} ${context.endpoint}`, validationError);
    }

    // Emit event for UI
    if (this.showUI && typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('api-validation-error', {
          detail: validationError,
        }),
      );
    }

    // Throw in strict mode
    if (this.strictMode) {
      throw new Error(`API Validation Failed: ${context.method} ${context.endpoint}`);
    }
  }

  /**
   * Apply the validator to an axios instance
   */
  applyToAxios(axiosInstance: AxiosInstance): void {
    const interceptor = this.createInterceptor();
    axiosInstance.interceptors.response.use(interceptor);

    if (this.enableConsoleLogging) {
      this.logger.info('🔍 Simple API Validator attached to axios instance');
    }
  }

  /**
   * Manually validate a response
   */
  validateResponse(response: AxiosResponse): void {
    const interceptor = this.createInterceptor();
    interceptor(response);
  }

  /**
   * Enable or disable validation
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Check if validation is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Add a new schema mapping
   */
  addSchema(pattern: string, schema: ZodSchema): void {
    this.schemas[pattern] = schema;
  }

  /**
   * Remove a schema mapping
   */
  removeSchema(pattern: string): void {
    delete this.schemas[pattern];
  }

  /**
   * Get all schema mappings
   */
  getSchemas(): Record<string, ZodSchema> {
    return { ...this.schemas };
  }

  /**
   * Store error for UI display
   */
  private storeError(key: string, validationError: ValidationError): void {
    if (!this.storedErrors[key]) {
      this.storedErrors[key] = {
        timestamp: validationError.timestamp.toISOString(),
        count: 0,
        uniqueErrors: {},
      };
    }

    this.storedErrors[key].count += 1;
    this.storedErrors[key].timestamp = validationError.timestamp.toISOString();

    // Store unique errors
    validationError.errors.forEach((err) => {
      const errorKey = `${err.path.join('.')}-${err.message}`;
      this.storedErrors[key].uniqueErrors[errorKey] = {
        path: err.path,
        message: err.message,
        receivedValue: validationError.response,
      };
    });

    // Notify subscribers
    this.notifySubscribers();
  }

  /**
   * Subscribe to error changes
   */
  subscribe(callback: () => void): () => void {
    this.subscribers.push(callback);
    return () => {
      const index = this.subscribers.indexOf(callback);
      if (index > -1) {
        this.subscribers.splice(index, 1);
      }
    };
  }

  /**
   * Get all stored errors
   */
  getErrors(): Record<string, StoredValidationError> {
    return { ...this.storedErrors };
  }

  /**
   * Clear all stored errors
   */
  clearAllErrors(): void {
    this.storedErrors = {};
    this.notifySubscribers();
  }

  /**
   * Clear error for specific endpoint
   */
  clearError(key: string): void {
    delete this.storedErrors[key];
    this.notifySubscribers();
  }

  /**
   * Notify all subscribers of changes
   */
  private notifySubscribers(): void {
    this.subscribers.forEach((callback) => callback());
  }
}
